import type { Game, GameContext, GameMeta, InputState } from '../types';
import { playExplosion, playPowerup, playShoot } from '../../audio/sfx';
import { GridSlicerEngine, type GridSlicerInput } from './GridSlicerEngine';
import { CanvasRenderer } from './CanvasRenderer';

export class GridSlicerGame implements Game {
  readonly meta: GameMeta = {
    id: 'grid-slicer',
    title: 'NEON GRID SLICER',
    blurb: 'Qix territory carver. Slice neon sectors, trap open-grid drones, trigger Sonic Pulses & powerups!',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private canvas!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private renderer!: CanvasRenderer;
  private engine!: GridSlicerEngine;
  private screenShake: number = 0;
  private rKeyPressed: boolean = false;
  private keyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'r' || e.key === 'R') {
      this.rKeyPressed = true;
    }
  };
  private clickHandler = () => {
    if (this.engine?.isGameOver) {
      this.rKeyPressed = true;
    }
  };

  mount(ctx: GameContext): void {
    this.ctx = ctx;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.setAttribute('aria-label', 'Neon Grid Slicer canvas');

    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.canvas);

    this.canvasCtx = this.canvas.getContext('2d')!;
    this.renderer = new CanvasRenderer(this.canvasCtx);

    window.addEventListener('keydown', this.keyDownHandler);
    this.canvas.addEventListener('click', this.clickHandler);

    const rect = ctx.mount.getBoundingClientRect();
    const w = rect.width || 800;
    const h = rect.height || 600;

    this.engine = new GridSlicerEngine({ cols: 80, rows: 60, width: w, height: h, initialLives: 3 });
    this.resize(w, h);
  }

  resize(w: number, h: number): void {
    if (!this.canvas) return;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;

    if (this.canvasCtx) {
      this.canvasCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  update(dt: number, input: InputState): void {
    if (!this.engine || !this.renderer) return;

    const bands = this.ctx.getBands();
    const energy = bands.energy;
    const kick = bands.kick;

    const restartRequested = input.firePressed || this.rKeyPressed;
    this.rKeyPressed = false;

    const engineInput: GridSlicerInput = {
      left: input.left,
      right: input.right,
      up: input.up,
      down: input.down,
      fire: input.fire,
      fastCut: input.fire,
      restart: restartRequested,
    };

    const prevScore = this.engine.score;
    const prevLives = this.engine.player.lives;
    const prevPercentage = this.engine.claimedPercentage;
    const prevPowerUps = this.engine.powerUps.length;

    this.engine.update(dt, engineInput, energy);

    // Audio SFX & Feedback
    if (this.engine.claimedPercentage > prevPercentage || this.engine.powerUps.length < prevPowerUps) {
      playPowerup();
      this.screenShake = 0.22;
    }
    if (this.engine.player.lives < prevLives) {
      playExplosion();
      this.screenShake = 0.5;
      if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
    }
    if (this.engine.score > prevScore && input.firePressed) {
      playShoot();
    }

    if (this.screenShake > 0) {
      this.screenShake = Math.max(0, this.screenShake - dt);
    }

    this.renderer.render(this.engine, kick, energy, this.screenShake);
  }

  getHud() {
    if (!this.engine) return { score: 0, lives: 3, wave: 1 };
    return {
      score: this.engine.score,
      lives: Math.max(0, this.engine.player.lives),
      wave: this.engine.wave,
      message: this.engine.messageTimer > 0 ? this.engine.message : undefined,
    };
  }

  dispose(): void {
    window.removeEventListener('keydown', this.keyDownHandler);
    if (this.canvas) {
      this.canvas.removeEventListener('click', this.clickHandler);
      if (this.canvas.parentElement) {
        this.canvas.parentElement.removeChild(this.canvas);
      }
    }
  }
}
